import { useState } from 'react'

import { env } from '@/app/env'
import { readMatrix, validateMatrixUrl } from '@/app/matrix'
import type { MatrixService } from '@/app/matrix'
import {
  MATRIX_SERVICES,
  selectionForUrl,
  urlForSelection
} from '@/features/matrix/domain/matrix-presets'
import type { MatrixServiceSelection } from '@/features/matrix/domain/matrix-presets'
import type { MatrixFormErrors, MatrixFormValues } from '@/features/matrix/types/matrix-form'

export type MatrixFormState = {
  selections: Record<MatrixService, MatrixServiceSelection>
  errors: MatrixFormErrors
  /** Whether a stored override is currently applied — drives the status line. */
  hasActiveOverride: boolean
  setPreset: (service: MatrixService, presetKey: string) => void
  setCustomUrl: (service: MatrixService, customUrl: string) => void
  /** Validates every row; hands the resolved URLs to onValid, or surfaces errors and stops. */
  submit: (onValid: (values: MatrixFormValues) => void) => void
}

/**
 * Edit state for the matrix override, seeded from the currently applied URLs so
 * the form opens on whatever the boot-time env resolution actually picked.
 *
 * The state lives here rather than in the form because the form's fields and its
 * actions render in different places: the /matrix page puts them under the form,
 * the dev-tools panel puts them in the modal footer. Both read one hook.
 */
export function useMatrixForm(): MatrixFormState {
  const [selections, setSelections] = useState(() => ({
    ara: selectionForUrl('ara', env.araUrl),
    scoping: selectionForUrl('scoping', env.scopingBaseUrl)
  }))
  const [errors, setErrors] = useState<MatrixFormErrors>({})

  function setPreset(service: MatrixService, presetKey: string) {
    setSelections(prev => ({ ...prev, [service]: { ...prev[service], presetKey } }))
    setErrors(prev => ({ ...prev, [service]: undefined }))
  }

  function setCustomUrl(service: MatrixService, customUrl: string) {
    setSelections(prev => ({ ...prev, [service]: { ...prev[service], customUrl } }))
    setErrors(prev => ({ ...prev, [service]: undefined }))
  }

  function submit(onValid: (values: MatrixFormValues) => void) {
    const values = {} as MatrixFormValues
    const nextErrors: MatrixFormErrors = {}
    for (const service of MATRIX_SERVICES) {
      const result = validateMatrixUrl(urlForSelection(service, selections[service]))
      if (!result.ok) {
        nextErrors[service] = result.reason
        continue
      }
      values[service] = result.normalized
    }
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return
    onValid(values)
  }

  return {
    selections,
    errors,
    hasActiveOverride: readMatrix() !== null,
    setPreset,
    setCustomUrl,
    submit
  }
}
